import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, onMounted, onUnmounted, computed } from "vue";
  import router from "../../router/index";
  import store from "@/store";
  import Cleave from "vue-cleave-component";
  import SliderLimited from "@/components/ui/SliderLimited.vue";
  import useVuelidate from "@vuelidate/core";
  import {
    required,
    email,
    minLength,
    numeric,
    helpers,
  } from "@vuelidate/validators";
  import { validPhoneNumber } from "@/helpers/validation";
  import HeaderCustomer from "@/components/HeaderCustomer.vue";
  import LotCardWaitlist from "@/components/eoi/LotCardWaitlist.vue";
  import { LotRangeInterface } from "@/types/LotRangeInterface";
  import { scrollTo } from "@/helpers/smoothScrollToElement";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { defaultRange } from "@/static/waitlist";
  import {
    changePrimaryColor,
    resetPrimaryColor,
  } from "@/helpers/customer/changePrimaryColor";
  import { isLogged } from "@/features/useUser";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EOIWaitlist',
  setup(__props, { expose: __expose }) {
  __expose();

  const estateId = ref(router.currentRoute.value.params.estateId);
  const estate = ref({});
  const customerId = store.state.user?.data?._id;
  let userProfile = {};
  const range: LotRangeInterface = defaultRange;
  const lotSize = ref([range.size.min, range.size.max]);
  const budget = ref([range.budget.min, range.budget.max]);
  const frontage = ref([range.frontage.min, range.frontage.max]);
  const showPage = ref(false);
  const isEmbedded = computed(() => router.currentRoute.value.query?.embed);
  const businessId = computed(
    () => router.currentRoute.value.query?.businessId
  );

  const waitlistForm = reactive({
    customerId: customerId,
    estateId: estateId.value,
    buyerDetails: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      postcode: "",
    },

    budget: {
      from: 0,
      to: 0,
    },
    lotSize: {
      from: 0,
      to: 0,
    },
    frontage: {
      from: 0,
      to: 0,
    },
  });
  const rules = computed(() => {
    return {
      buyerDetails: {
        firstName: { required },
        lastName: { required },
        email: { required, email },
        phone: {
          required,
          validPhoneNumber: waitlistForm.buyerDetails.phone
            ? helpers.withMessage(
                "Value must be valid phone number",
                validPhoneNumber
              )
            : true,
        },
        postcode: { numeric, minLength: minLength(4) },
      },
    };
  });
  const v$ = useVuelidate(rules, waitlistForm);

  onMounted(() => {
    store.dispatch("estate/getEstate", estateId.value).then((res) => {
      estate.value = res.data;
      changePrimaryColor(estate.value.iconColor);
    });

    if (isLogged()) {
      store
        .dispatch("subscription/getSubscription", {
          estateId: estateId.value,
          customerId,
        })
        .then((res) => {
          if (res.data.length === 0) {
            showPage.value = true;
          } else {
            router.replace(`/customer/waitlist/${res.data[0]._id}`);
          }
        });
    } else {
      showPage.value = true;
    }
  });

  onUnmounted(() => {
    resetPrimaryColor();
  });

  const getUserProfile = () => {
    store.dispatch("user/getUser", customerId).then((res) => {
      userProfile = res.data;
      waitlistForm.buyerDetails.firstName = res.data.firstName;
      waitlistForm.buyerDetails.lastName = res.data.lastName;
      waitlistForm.buyerDetails.email = res.data.email;
      waitlistForm.buyerDetails.phone = res.data.phone;
      waitlistForm.buyerDetails.postcode = res.data.detailsId.postcode
        ? res.data.detailsId.postcode
        : res.data.detailsId.state;
    });
  };

  if (isLogged()) {
    getUserProfile();
  }

  const updateUserProfileDetails = () => {
    if (
      userProfile.detailsId?.state ||
      userProfile.detailsId?.postcode !== waitlistForm.buyerDetails.postcode
    ) {
      store.dispatch("customer/updateCustomerDetails", {
        id: userProfile.detailsId._id,
        data: {
          state: waitlistForm.buyerDetails.postcode,
        },
      });
    }
  };

  function updateUserProfile() {
    const updateUserProfileData = {};

    updateUserProfileData.data = JSON.parse(
      JSON.stringify(waitlistForm.buyerDetails)
    );
    delete updateUserProfileData.data.email;
    delete updateUserProfileData.data.state;

    if (userProfile.firstName === updateUserProfileData.data.firstName) {
      delete updateUserProfileData.data.firstName;
    }

    if (userProfile.lastName === updateUserProfileData.data.lastName) {
      delete updateUserProfileData.data.lastName;
    }

    if (userProfile.phone === updateUserProfileData.data.phone) {
      delete updateUserProfileData.data.phone;
    }

    if (Object.keys(updateUserProfileData.data).length > 0) {
      updateUserProfileData.id = customerId;
      store.dispatch("user/updateUser", updateUserProfileData);
    }

    updateUserProfileDetails();
  }

  const submit = () => {
    v$.value.$touch();
    setTimeout(() => {
      const errors = document.getElementsByClassName("error");

      errors[0] && scrollTo(errors[0], 100);
    });

    if (v$.value.$invalid) {
      return;
    }

    waitlistForm.budget.from = budget.value[0];
    waitlistForm.budget.to = budget.value[1];
    waitlistForm.lotSize.from = lotSize.value[0];
    waitlistForm.lotSize.to = lotSize.value[1];
    waitlistForm.frontage.from = frontage.value[0];
    waitlistForm.frontage.to = frontage.value[1];

    if (isLogged()) {
      updateUserProfile();
      const waitlistItem = JSON.parse(JSON.stringify(waitlistForm));

      delete waitlistItem.buyerDetails;
      waitlistItem.landownerId = estate.value?.landownerId?._id;
      store.dispatch("subscription/addToWaitlist", waitlistItem).then(() => {
        if (isEmbedded.value) {
          router.push(
            `/estate/${estateId.value}/waitlist-confirmation?embed=true`
          );
        } else {
          router.push(`/estate/${estateId.value}/waitlist-confirmation`);
        }
      });
    } else {
      const waitlistItem = {
        personData: {
          estateId: estateId.value,
          email: waitlistForm.buyerDetails.email,
          firstName: waitlistForm.buyerDetails.firstName,
          lastName: waitlistForm.buyerDetails.lastName,
          phone: waitlistForm.buyerDetails.phone,
          postcode: waitlistForm.buyerDetails.postcode,
          businessId: businessId.value,
        },
        criteria: {
          budget: waitlistForm.budget,
          lotSize: waitlistForm.lotSize,
          frontage: waitlistForm.frontage,
        },
      };

      store
        .dispatch("subscription/addSubscriptionForLead", waitlistItem)
        .then(() => {
          if (isEmbedded.value) {
            router.push(
              `/estate/${estateId.value}/waitlist-confirmation?embed=true`
            );
          } else {
            router.push(`/estate/${estateId.value}/waitlist-confirmation`);
          }
        });
    }
  };

const __returned__ = { estateId, estate, customerId, get userProfile() { return userProfile }, set userProfile(v) { userProfile = v }, range, lotSize, budget, frontage, showPage, isEmbedded, businessId, waitlistForm, rules, v$, getUserProfile, updateUserProfileDetails, updateUserProfile, submit, ref, reactive, onMounted, onUnmounted, computed, get router() { return router }, get store() { return store }, get Cleave() { return Cleave }, SliderLimited, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get minLength() { return minLength }, get numeric() { return numeric }, get helpers() { return helpers }, get validPhoneNumber() { return validPhoneNumber }, HeaderCustomer, LotCardWaitlist, get LotRangeInterface() { return LotRangeInterface }, get scrollTo() { return scrollTo }, get phoneNumberOption() { return phoneNumberOption }, get defaultRange() { return defaultRange }, get changePrimaryColor() { return changePrimaryColor }, get resetPrimaryColor() { return resetPrimaryColor }, get isLogged() { return isLogged } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})